import LeaderboardStats from './LeaderboardStats';
import React from 'react';
import { AlreadyPlayedPage, DateSelector, GameBrand, LeaderboardFrame, formatLeaderboardDate } from '@precisionpros/game-ui';
import { DateTime } from 'luxon';
import GAME_CONFIG from './config/game';
import { API_URL, HUB_DISPLAY_NAME, HUB_URL, HUB_USER_ID } from './config/environment';
import { chooseEasyLeaderboard, getDailyStatus, getHistory, getLeaderboard, getPossibleWords, getStatistics, startGame, startTimer, submitGuess } from './api/wordpro';
import './index.css';

const leaderboardRecordHeaders = HUB_USER_ID ? { 'X-Hub-User-Id': HUB_USER_ID, 'X-Hub-Display-Name': HUB_DISPLAY_NAME } : undefined;

function App() {
  const pathView = window.location.pathname === '/leaderboard'
    ? 'leaderboard'
    : (window.location.pathname === '/how-to-play' ? 'how-to' : null);
  const requestedView = pathView || new URLSearchParams(window.location.search).get('view');
  const [mode, setMode] = React.useState(() => {
    const savedMode = typeof window !== 'undefined' ? localStorage.getItem('wordproLastMode') : null;
    return ['easy', 'medium', 'hard'].includes(savedMode) ? savedMode : 'medium';
  });
  const [session, setSession] = React.useState(null);
  const [statusReady, setStatusReady] = React.useState(false);
  const [guess, setGuess] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [leaderboard, setLeaderboard] = React.useState(null);
  const [leaderboardDate, setLeaderboardDate] = React.useState(() => DateTime.now().setZone(GAME_CONFIG.timezone).startOf('day'));
  const [selectedLeaderboardEntry, setSelectedLeaderboardEntry] = React.useState(null);
  const [possibleWordsModal, setPossibleWordsModal] = React.useState(null);
  const [possibleWordsLoading, setPossibleWordsLoading] = React.useState(false);
  const [possibleWordCounts, setPossibleWordCounts] = React.useState({});
  const [history, setHistory] = React.useState(null);
  const [statistics, setStatistics] = React.useState(null);
  const [showInstructions, setShowInstructions] = React.useState(false);
  const [easyChoiceResolved, setEasyChoiceResolved] = React.useState(false);
  const guessInputRef = React.useRef(null);
  const currentGuessRef = React.useRef(null);
  const guessesRef = React.useRef(null);
  const timerStartPromiseRef = React.useRef(null);
  const [cursorPosition, setCursorPosition] = React.useState(0);
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);

  React.useEffect(() => {
    let mounted = true;
    const sendToAuthentication = () => {
      if (typeof window !== 'undefined') {
        window.location.replace(HUB_URL);
      }
    };
    if (requestedView === 'how-to') {
      if (mounted) setStatusReady(true);
      return () => { mounted = false; };
    }
    if (requestedView === 'leaderboard') {
      // Results can render as soon as their API responds. Shared shortcuts
      // load independently and must not hold up the leaderboard.
      setBusy(true);
      getLeaderboard(leaderboardDate.toISODate(), 1, 10).then((leaderboardResponse) => {
        if (!mounted) return;
        setLeaderboard(leaderboardResponse);
        setStatusReady(true);
      }).catch((requestError) => {
        if (requestError?.status === 401 || requestError?.status === 403) {
          sendToAuthentication();
        } else if (mounted) {
          setError(requestError.message);
          setStatusReady(true);
        }
      }).finally(() => {
        if (mounted) setBusy(false);
      });
      return () => { mounted = false; };
    }
    const dailyStatusPromise = getDailyStatus();
    const leaderboardPromise = Promise.resolve();
    Promise.all([dailyStatusPromise, leaderboardPromise]).then(async ([response]) => {
      if (requestedView === 'leaderboard') {
        if (mounted && response?.session) {
          setSession(response.session);
          setEasyChoiceResolved(!response.session.easy_choice_pending);
        }
        await leaderboardPromise;
        if (mounted) setStatusReady(true);
        return;
      }
      if (mounted && response?.session) {
        setSession(response.session);
        if (response.session.completed && response.session.mode) {
          localStorage.setItem('wordproLastMode', response.session.mode);
        }
        setEasyChoiceResolved(!response.session.easy_choice_pending);
      } else if (mounted) {
        // Let the player choose a mode before creating today's session.
        setStatusReady(true);
      }
      if (mounted && response?.session) setStatusReady(true);
    }).catch((requestError) => {
      if (requestError?.status === 401 || requestError?.status === 403) {
        sendToAuthentication();
      } else if (mounted) {
        setError(requestError.message);
        setStatusReady(true);
      }
    });
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    if (!session || session.completed) return undefined;
    setCursorPosition(0);
    const focusNewGuess = window.requestAnimationFrame(() => currentGuessRef.current?.focus());
    return () => window.cancelAnimationFrame(focusNewGuess);
  }, [session?.id, session?.guesses?.length, session?.completed]);

  React.useEffect(() => {
    const guessesElement = guessesRef.current;
    if (guessesElement) guessesElement.scrollTop = guessesElement.scrollHeight;
  }, [session?.guesses?.length]);

  React.useEffect(() => {
    if (!session || session.mode === 'easy' || !session.started_at) {
      setElapsedSeconds(0);
      return undefined;
    }
    const updateElapsed = () => {
      if (session.completed && session.result?.elapsed_seconds != null) {
        setElapsedSeconds(Number(session.result.elapsed_seconds));
      } else {
        setElapsedSeconds(Math.max((Date.now() - Date.parse(session.started_at)) / 1000, 0));
      }
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 100);
    return () => window.clearInterval(timer);
  }, [session]);

  const isGuessComplete = guess.length === 5 && /^[A-Z]{5}$/.test(guess);

  const beginGame = async () => {
    setBusy(true);
    setError('');
    try {
      timerStartPromiseRef.current = null;
      setSession(await startGame(mode));
      setEasyChoiceResolved(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const startTimerOnFirstLetter = () => {
    if (!session || session.completed || session.mode === 'easy' || session.started_at || timerStartPromiseRef.current) return;
    const timerRequest = startTimer ? startTimer(session.id) : Promise.resolve(session);
    timerStartPromiseRef.current = Promise.resolve(timerRequest || session).then((startedSession) => {
      setSession((current) => current?.id === startedSession.id ? startedSession : current);
      return startedSession;
    }).catch((requestError) => {
      timerStartPromiseRef.current = null;
      setError(requestError.message);
      throw requestError;
    });
  };

  const submitCurrentGuess = async () => {
    if (!session || !isGuessComplete || busy) return;
    setBusy(true);
    setError('');
    try {
      if (timerStartPromiseRef.current) await timerStartPromiseRef.current;
      const response = await submitGuess(session.id, guess);
      setGuess('');
      setSession(response.session ? response.session : {
        ...session,
        guesses: [...(session.guesses || []), response.guess],
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const makeGuess = async (event) => {
    event.preventDefault();
    await submitCurrentGuess();
  };

  const keyboardRows = ['QWERTYUIOP', 'ASDFGHJKL'];
  const finalKeyboardRow = 'ZXCVBNM'.split('');
  const keyboardFeedback = (letter) => {
    const feedback = (session?.guesses || [])
      .filter((item) => item.word.includes(letter))
      .flatMap((item) => item.word.split('').map((character, index) => character === letter ? item.feedback[index] : null))
      .filter(Boolean);
    if (feedback.includes('G')) return 'green';
    if (feedback.includes('Y')) return 'yellow';
    if (feedback.includes('B')) return 'gray';
    return '';
  };

  const pressKey = async (key) => {
    if (key === 'ENTER') return submitCurrentGuess();
    if (key === '⌫') {
      if (cursorPosition > 0) {
        setGuess((value) => {
          const slots = value.padEnd(5, ' ').split('');
          slots[cursorPosition - 1] = ' ';
          return slots.join('').trimEnd();
        });
        setCursorPosition(cursorPosition - 1);
      }
      return;
    }
    if (cursorPosition < 5) {
      startTimerOnFirstLetter();
      setGuess((value) => {
        const slots = value.padEnd(5, ' ').split('');
        slots[cursorPosition] = key;
        return slots.join('').replace(/\s+$/, '');
      });
      setCursorPosition(cursorPosition + 1);
    }
  };

  const focusGuessPosition = (index) => {
    currentGuessRef.current?.focus();
    setCursorPosition(index);
  };

  const handleBoardKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (guess.length === 5) submitCurrentGuess();
      return;
    }
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (cursorPosition > 0) {
        setGuess((value) => {
          const slots = value.padEnd(5, ' ').split('');
          slots[cursorPosition - 1] = ' ';
          return slots.join('').trimEnd();
        });
        setCursorPosition(cursorPosition - 1);
      }
      return;
    }
    if (/^[a-zA-Z]$/.test(event.key)) {
      event.preventDefault();
      startTimerOnFirstLetter();
      const letter = event.key.toUpperCase();
      setGuess((value) => {
        const slots = value.padEnd(5, ' ').split('');
        slots[cursorPosition] = letter;
        return slots.join('').replace(/\s+$/, '');
      });
      setCursorPosition(Math.min(cursorPosition + 1, 5));
    }
  };

  const renderGuessTiles = (word, feedback = '', editable = false, activeIndex = -1) => [...Array(5)].map((_, index) => {
    const letter = word[index] && word[index] !== ' ' ? word[index] : '';
    const tileClass = feedback[index] ? `feedback-${feedback[index].toLowerCase()}` : (letter ? 'draft' : 'empty');
    const activeClass = editable && activeIndex === index ? ' active' : '';
    return <span className={`wp2-letter-tile ${tileClass}${editable ? ' editable' : ''}${activeClass}`} key={`${index}-${letter}`} onClick={editable ? () => focusGuessPosition(index) : undefined}>{letter}</span>;
  });

  const formattedElapsed = `${elapsedSeconds.toFixed(1)}s`;

  const handleEasyChoice = async (useForLeaderboard) => {
    setBusy(true);
    setError('');
    try {
      await chooseEasyLeaderboard(session.id, useForLeaderboard);
      setEasyChoiceResolved(true);
      setSession({ ...session, easy_choice_pending: false, result: { ...session.result, leaderboard_opt_in: useForLeaderboard, leaderboard_mode: useForLeaderboard ? 'medium' : 'easy' } });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const loadLeaderboard = async (dateValue, page = 1) => {
    setBusy(true);
    setError('');
    try {
      setLeaderboard(await getLeaderboard(dateValue.toISODate(), page, 10));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const viewLeaderboard = async () => loadLeaderboard(leaderboardDate, 1);

  const changeLeaderboardDate = async (days) => {
    const nextDate = leaderboardDate.plus({ days });
    setLeaderboardDate(nextDate);
    setSelectedLeaderboardEntry(null);
    setPossibleWordsModal(null);
    await loadLeaderboard(nextDate, 1);
  };

  const changeLeaderboardPage = async (page) => loadLeaderboard(leaderboardDate, page);

  const openPossibleWords = async (entry, guessItem) => {
    setPossibleWordsLoading(true);
    try {
      const response = await getPossibleWords(entry.id, guessItem.sequence);
      setPossibleWordCounts((current) => ({ ...current, [`${entry.id}:${guessItem.sequence}`]: response.count }));
      setPossibleWordsModal({ entry, guess: guessItem, words: response.words || [] });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setPossibleWordsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!selectedLeaderboardEntry?.guesses?.length) return undefined;
    let active = true;
    Promise.all(selectedLeaderboardEntry.guesses.map(async (guess) => {
      try {
        const response = await getPossibleWords(selectedLeaderboardEntry.id, guess.sequence);
        return [`${selectedLeaderboardEntry.id}:${guess.sequence}`, response.count];
      } catch (_) {
        return null;
      }
    })).then((counts) => {
      if (!active) return;
      setPossibleWordCounts((current) => ({
        ...current,
        ...Object.fromEntries(counts.filter(Boolean)),
      }));
    });
    return () => { active = false; };
  }, [selectedLeaderboardEntry]);

  const viewHistory = async () => {
    setBusy(true);
    setError('');
    try {
      setHistory(await getHistory());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const viewStatistics = async () => {
    setBusy(true);
    setError('');
    try {
      setStatistics(await getStatistics());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const completed = session?.completed;
  const easyChoicePending = completed && session.mode === 'easy' && !easyChoiceResolved;

  const formatLeaderboardTime = (seconds) => {
    if (seconds == null) return '—';
    const wholeSeconds = Math.floor(Number(seconds));
    const milliseconds = Math.round((Number(seconds) - wholeSeconds) * 1000);
    return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
  };

  const rankLabel = (index) => ['🥇', '🥈', '🥉'][index] || index + 1;

  const renderLeaderboard = () => {
    const page = leaderboard.page || 1;
    const pageSize = leaderboard.page_size || 10;
    const total = leaderboard.total || 0;
    const rankOffset = (page - 1) * pageSize;
    return (
      <div className="wp2-leaderboard-grid">
        <section className="wp2-leaderboard-tile wp2-today-results">
          <h2>Today’s Results</h2>
          {leaderboard.hidden ? <p>Leaderboard results appear after you play today.</p> : leaderboard.results.length ? (
            <table className="wp2-leaderboard-table">
              <thead><tr><th>Rank</th><th>Name</th><th>Mode</th><th>Time</th><th>Tries</th></tr></thead>
              <tbody>{leaderboard.results.map((entry, index) => (
                <tr key={entry.id} className={entry.player_name === HUB_DISPLAY_NAME ? 'current-player' : ''}>
                  <td>{entry.rank == null ? '—' : rankLabel(entry.rank - 1)}</td><td><button type="button" className="wp2-player-link" onClick={() => setSelectedLeaderboardEntry(entry)}>{entry.player_name}</button>{entry.player_name === HUB_DISPLAY_NAME && <small> you</small>}</td><td>{entry.mode}</td><td>{entry.mode === 'easy' ? '—' : formatLeaderboardTime(entry.elapsed_seconds)}</td><td>{entry.attempts}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p className="wp2-muted">No scores for this day.</p>}
          {!leaderboard.hidden && total > pageSize && <div className="wp2-leaderboard-pagination"><button type="button" onClick={() => changeLeaderboardPage(page - 1)} disabled={page <= 1 || busy}>Previous</button><span>{rankOffset + 1}–{Math.min(rankOffset + pageSize, total)} of {total}</span><button type="button" onClick={() => changeLeaderboardPage(page + 1)} disabled={rankOffset + pageSize >= total || busy}>Next</button></div>}
        </section>

        <LeaderboardStats endpoint={`${API_URL}/wordpro/leaderboard-records/`} date={leaderboardDate.toISODate()} headers={leaderboardRecordHeaders} />
        {selectedLeaderboardEntry && <div className="wp2-guesses-modal" role="dialog" aria-modal="true" aria-label={`${selectedLeaderboardEntry.player_name}'s guesses`}>
          <div className="wp2-guesses-modal-content">
            <button type="button" className="wp2-modal-close" onClick={() => setSelectedLeaderboardEntry(null)}>Close</button>
            <h2>{selectedLeaderboardEntry.player_name}&apos;s guesses</h2>
            {selectedLeaderboardEntry.guesses?.length ? selectedLeaderboardEntry.guesses.map((item) => { const count = possibleWordCounts[`${selectedLeaderboardEntry.id}:${item.sequence}`]; return <div className="wp2-modal-guess" key={item.sequence}><div className="wp2-guess-tiles">{renderGuessTiles(item.word, item.feedback)}</div><button type="button" className="wp2-words-remaining" onClick={() => openPossibleWords(selectedLeaderboardEntry, item)} disabled={possibleWordsLoading} aria-label={`${count ?? 'remaining'} possible words after ${item.word}`}>{count ?? '…'}</button></div>; }) : <p className="wp2-muted">No guesses available.</p>}
          </div>
        </div>}
        {possibleWordsModal && <div className="wp2-guesses-modal" role="dialog" aria-modal="true" aria-label={`Possible words after ${possibleWordsModal.guess.word}`}>
          <div className="wp2-guesses-modal-content">
            <button type="button" className="wp2-modal-close" onClick={() => setPossibleWordsModal(null)}>Close</button>
            <h2>Possible words</h2>
            <p className="wp2-muted">After guess {possibleWordsModal.guess.word}: {possibleWordsModal.words.length}</p>
            <div className="wp2-possible-word-list">{possibleWordsModal.words.map((word) => <span key={word}>{word}</span>)}</div>
          </div>
        </div>}
      </div>
    );
  };

  if (requestedView === 'leaderboard' && !leaderboard) {
    return (
      <main className="wp2-shell" style={{ '--wp2-accent': GAME_CONFIG.accent }}>
        <GameBrand game={GAME_CONFIG} size="header" />
        <section className="wp2-card wp2-leaderboard-loading">
          <h1>Leaderboard</h1>
          <p>{error || 'Loading leaderboard…'}</p>
        </section>
      </main>
    );
  }

  if (completed && !easyChoicePending && !leaderboard) {
    return <AlreadyPlayedPage
      game={GAME_CONFIG}
      title="You already solved today’s puzzle!"
      message="Come back tomorrow for a new puzzle."
      detail={`You solved it in ${session.result.attempts} guesses${session.mode !== 'easy' ? ` · ${formattedElapsed}` : ''}.`}
      leaderboardHref={`${window.location.origin}/leaderboard`}
      homeHref={HUB_URL}
    />;
  }

  if (leaderboard) {
    return (
      <LeaderboardFrame
        game={GAME_CONFIG}
        currentGameKey={GAME_CONFIG.key}
        title="Leaderboard"
        homeHref={HUB_URL}
        showPrePlayBanner={leaderboard.hidden}
        playHref={window.location.origin}
        dateSelector={<DateSelector dateLabel={formatLeaderboardDate(leaderboardDate.toISODate())} onPrevious={() => changeLeaderboardDate(-1)} onNext={() => changeLeaderboardDate(1)} />}
      >
        <div className="wp2-leaderboard-frame-content">{renderLeaderboard()}</div>
      </LeaderboardFrame>
    );
  }

  if (requestedView === 'how-to') {
    return (
      <main className="wp2-shell" style={{ '--wp2-accent': GAME_CONFIG.accent }}>
        <GameBrand game={GAME_CONFIG} size="header" />
        <section className="wp2-card wp2-how-to">
          <h1>How to Play WordPro</h1>
          <p>Guess the five-letter word in as few guesses as possible.</p>
          <ol>
            <li>Enter any valid five-letter word.</li>
            <li>Green letters are correct and in the right position.</li>
            <li>Yellow letters are in the word but in a different position.</li>
            <li>Gray letters are not in the answer.</li>
          </ol>
          <p>The daily puzzle changes each Arizona calendar day. You can use the available game modes and unlimited guesses to solve it.</p>
          <a className="wp2-secondary wp2-link-button" href={HUB_URL}>Back to games</a>
        </section>
      </main>
    );
  }

  return (
    <main className="wp2-shell" style={{ '--wp2-accent': GAME_CONFIG.accent }}>
      <GameBrand game={GAME_CONFIG} size="header" />
      <section className={`wp2-card ${session && !completed ? 'wp2-playing' : ''} ${completed ? 'wp2-completed' : ''}`}>
        <h1 className="sr-only">WordPro</h1>
        {statusReady && !session && !leaderboard && !history && !statistics && error && <p className="wp2-error" role="alert">{error}</p>}

        {statusReady && !session && !leaderboard && !history && !statistics && (
          <div className="wp2-mode-picker">
            <h2>Choose your mode</h2>
            <p className="wp2-muted">Select a mode before starting today&apos;s puzzle.</p>
            <div className="wp2-mode-options" role="radiogroup" aria-label="Game mode">
              {[
                ['easy', 'Easy', 'Previous clues are optional; you may guess any valid word. No timer displayed.'],
                ['medium', 'Medium', 'Previous clues are optional; you may guess any valid word. Timed.'],
                ['hard', 'Hard', 'Previous clues must be followed. Timed.'],
              ].map(([value, label, description]) => (
                <button
                  type="button"
                  key={value}
                  className={`wp2-mode-option${mode === value ? ' selected' : ''}`}
                  role="radio"
                  aria-checked={mode === value}
                  onClick={() => setMode(value)}
                >
                  <strong>{label}</strong>
                  <span>{description}</span>
                </button>
              ))}
            </div>
            <button type="button" onClick={beginGame} disabled={busy}>Start {mode[0].toUpperCase() + mode.slice(1)} game</button>
          </div>
        )}

        {!leaderboard && session && !completed && (
          <>
            <div className="wp2-game-meta" aria-live="off">
              {session.mode !== 'easy' && <span>Time elapsed: {formattedElapsed}</span>}
              <span>Possible words: {session.possible_words_remaining ?? '—'}</span>
            </div>
            {error && <p className="wp2-error wp2-game-error" role="alert">{error}</p>}
            <div ref={guessesRef} className="wp2-guesses" aria-live="polite">
              {(session.guesses || []).map((item) => <div className="wp2-guess" key={item.sequence || item.word} aria-label={`Guess ${item.word}`}><div className="wp2-guess-tiles">{renderGuessTiles(item.word, item.feedback)}</div></div>)}
              <div ref={currentGuessRef} className="wp2-guess wp2-current-guess" aria-label="Current guess" tabIndex="0" onKeyDown={handleBoardKeyDown} onClick={() => currentGuessRef.current?.focus()}><div className="wp2-guess-tiles">{renderGuessTiles(guess, '', true, cursorPosition < 5 ? cursorPosition : -1)}</div></div>
            </div>
            <form onSubmit={makeGuess} className="wp2-guess-form">
              <input ref={guessInputRef} id="guess" aria-label="Enter a guess" className="wp2-typing-input" value={guess} maxLength={5} autoComplete="off" inputMode="text" onChange={(event) => { const next = event.target.value.replace(/[^a-z]/gi, '').slice(0, 5).toUpperCase(); if (next && !guess) startTimerOnFirstLetter(); setGuess(next); setCursorPosition(next.length); }} />
            </form>
            <div className="wp2-keyboard" aria-label="On-screen keyboard">
              {keyboardRows.map((row) => <div className="wp2-keyboard-row" key={row}>{row.split('').map((letter) => <button type="button" key={letter} className={`wp2-key ${keyboardFeedback(letter)}`} onClick={() => pressKey(letter)} disabled={busy}>{letter}</button>)}</div>)}
              <div className="wp2-keyboard-row wp2-keyboard-final-row">
                <button type="button" className="wp2-key wp2-key-wide" onClick={() => pressKey('ENTER')} disabled={busy || !isGuessComplete}>Enter</button>
                {finalKeyboardRow.map((letter) => <button type="button" key={letter} className={`wp2-key ${keyboardFeedback(letter)}`} onClick={() => pressKey(letter)} disabled={busy}>{letter}</button>)}
                <button type="button" className="wp2-key wp2-key-wide" onClick={() => pressKey('⌫')} disabled={busy}>⌫</button>
              </div>
            </div>
          </>
        )}

        {!leaderboard && completed && (
          <div className="wp2-completion">
            <h2>Puzzle complete</h2>
            <p>You solved it in {session.result.attempts} guesses.</p>
            {session.mode !== 'easy' && <p>Time elapsed: {formattedElapsed}</p>}
            {easyChoicePending && (
              <>
                <p>Your recorded time was {Number(session.result.elapsed_seconds).toFixed(1)} seconds. Use it for timed leaderboard rankings or keep as easy?</p>
                <button type="button" onClick={() => handleEasyChoice(true)} disabled={busy}>Use my time</button>
                <button type="button" className="wp2-secondary" onClick={() => handleEasyChoice(false)} disabled={busy}>Keep as Easy</button>
              </>
            )}
            {!easyChoicePending && <button type="button" onClick={viewLeaderboard} disabled={busy}>View leaderboard</button>}
          </div>
        )}

        {history && (
          <div className="wp2-history">
            <h2>Game history</h2>
            {!history.results.length && <p className="wp2-muted">No completed games yet.</p>}
            {history.results.map((item) => <article className="wp2-history-item" key={item.id}><strong>{item.puzzle_date}</strong><span>{item.answer} · {item.result.attempts} guesses</span><small>{item.guesses.map((entry) => entry.word).join(' → ')}</small></article>)}
            <button type="button" className="wp2-secondary" onClick={() => setHistory(null)}>Back to game</button>
          </div>
        )}

        {statistics && (
          <div className="wp2-statistics">
            <h2>Your statistics</h2>
            <dl><dt>Games played</dt><dd>{statistics.played_games}</dd><dt>Current streak</dt><dd>{statistics.current_streak}</dd><dt>Fastest timed solve</dt><dd>{statistics.fastest_time_seconds == null ? '—' : `${Number(statistics.fastest_time_seconds).toFixed(1)}s`}</dd><dt>First places</dt><dd>{statistics.first_place_finishes}</dd><dt>Top-three finishes</dt><dd>{statistics.top_three_finishes}</dd></dl>
            <button type="button" className="wp2-secondary" onClick={() => setStatistics(null)}>Back to game</button>
          </div>
        )}

      </section>
    </main>
  );
}

export default App;
